import React from 'react';

import { FIELD_NAME } from '../constants/form-constants';
import { useFeatureServices } from '../contexts/oneformv2-context';
import { dealerEntity } from '../entities/dealer-entity';
import { formService } from '../form-service';
import { getBrand } from '../helpers/carline-helpers';
import { sanitizeKvps } from '../helpers/dealer-helpers';
import { getFormName, getFormType, getFormVersion, getPageCategory } from '../helpers/form-helpers';
import { OkapiConsolidatedData } from '../types/vehicle-types';
import { createTrackingLink, getBaseEventInfo, getCarlineConfigurationForTracking } from './tracking-helper';
import { emptyConfig, emptyLatLongZip, emptyVehiclePreconfiguration } from './tracking-objects';
import {
	ErrorFormFields,
	OffersTrackingEvent,
	OneFormTrackingManager,
	TrackingError,
	TrackingEvent,
	TrackingEventInfo,
	TrackingForm,
} from './tracking-types';

export function useTrackingManager(): OneFormTrackingManager {
	const { tracking: trackingManager } = useFeatureServices();

	return React.useMemo((): OneFormTrackingManager => {
		function track(action: string, event: OffersTrackingEvent): void {
			if (trackingManager) {
				trackingManager.track(action, event as TrackingEvent);
			}
		}

		return {
			trackFormLayerLoad(
				formName: string,
				brand: string | undefined,
				pageCategory: string,
				vehiclePreconfiguration?: OkapiConsolidatedData,
			) {
				const emptyConfigLoad = { ...emptyConfig };
				delete emptyConfigLoad.PriceTypeParameter;
				delete emptyConfigLoad.PriceType;

				track('VWBasic_FormLayer_Load', {
					personalization: { status: false },
					form: {
						FormType: getFormType(),
						FormName: getFormName(true),
						FormVersion: getFormVersion(true),
						NewsletterType: undefined,
						CycleID: undefined,
						FormStart: true,
					},
					process: {
						Name: formName,
						StepName: 'SingleStep',
						StepNumber: '1',
					},
					eventInfo: {
						...getBaseEventInfo(pageCategory, brand),
						contentId: formName,
					},
					configuration: { ...emptyConfigLoad, ...getCarlineConfigurationForTracking(vehiclePreconfiguration) },
					...(dealerEntity.kvps && {
						partner: {
							informationBnr: sanitizeKvps(dealerEntity.kvps) ?? undefined,
							informationName: dealerEntity.name,
							informationZIP: dealerEntity.postalCode,
							informationBrand: brand,
							informationCountry: 'ESP',
						},
					}),
				});
			},

			trackFormCloseClick(eventInfo: TrackingEventInfo) {
				const vehiclePreconfiguration = formService.getFieldValue(FIELD_NAME.VEHICLE_PRECONFIGURATION);
				const baseEventInfoCloseClick = { ...getBaseEventInfo(getPageCategory(), getBrand()) };
				delete baseEventInfoCloseClick.templateType;

				track('VWBasic_FormClose_Click', {
					personalization: { status: false },
					form: {
						FormType: getFormType(),
						FormName: getFormName(true),
						PrefContactChannels: undefined,
						SourceID: undefined,
						ErrorFormFields: undefined,
						OpportunityUID: undefined,
						FormVersion: getFormVersion(true),
						CycleID: undefined,
						FormFields: undefined,
					},
					process: {
						Name: getFormName(),
						StepName: 'SingleStep',
						StepNumber: '1',
					},
					eventInfo: { ...baseEventInfoCloseClick, ...eventInfo },
					configuration: {
						...emptyVehiclePreconfiguration,
						...getCarlineConfigurationForTracking(vehiclePreconfiguration),
					},
					...(dealerEntity.kvps && {
						partner: {
							informationBnr: sanitizeKvps(dealerEntity.kvps) ?? undefined,
							informationName: dealerEntity.name,
							informationZIP: dealerEntity.postalCode,
							informationBrand: getBrand(),
							informationCountry: 'ESP',
						},
					}),
					link: createTrackingLink(eventInfo.contentId),
				});
			},

			trackFormSubmissionSuccessMessageLoad(eventInfo: TrackingEventInfo, formInfo?: TrackingForm) {
				const vehiclePreconfiguration = formService.getFieldValue(FIELD_NAME.VEHICLE_PRECONFIGURATION);

				track('VWBasic_FormSubmissionSuccessMessage_Load', {
					personalization: { status: false },
					form: {
						FormType: getFormType(),
						FormName: getFormName(true),
						PrefContactChannels: undefined,
						FormVersion: getFormVersion(true),
						NewsletterType: undefined,
						ErrorFormFields: undefined,
						Questionnaire: undefined,
						SourceID: undefined,
						OpportunityUID: undefined,
						CycleID: undefined,
						FormStart: false,
						LeadID: undefined,
						...formInfo,
					},
					process: {
						Name: getFormName(),
						StepName: 'ThankYou',
						StepNumber: '2',
					},
					eventInfo: { ...getBaseEventInfo(getPageCategory(), getBrand()), ...eventInfo },
					user: { ...emptyLatLongZip },
					configuration: {
						...emptyVehiclePreconfiguration,
						...getCarlineConfigurationForTracking(vehiclePreconfiguration),
					},
					...(dealerEntity.dealer && {
						partner: {
							informationBnr: sanitizeKvps(dealerEntity.kvps) ?? undefined,
							informationName: dealerEntity.name,
							informationZIP: dealerEntity.postalCode,
							informationBrand: getBrand(),
							informationCountry: 'ESP',
						},
					}),
					ownership: {},
				});
			},

			trackFormErrorMessageLoad(errorInfo?: TrackingError, errorFields?: ErrorFormFields[]) {
				track('VWBasic_FormErrorMessage_Load', {
					personalization: { status: false },
					form: {
						FormType: getFormType(),
						FormName: getFormName(true),
						FormVersion: getFormVersion(true),
						ErrorFormFields: errorFields ? { FieldNameandID: [...errorFields] } : undefined,
						ErrorAdressValidationFields: undefined,
					},
					process: {
						Name: getFormName(),
						StepName: 'SingleStep',
						StepNumber: '1',
					},
					eventInfo: { ...getBaseEventInfo(getPageCategory(), getBrand()) },
					error: errorInfo ? { message: errorInfo.message, code: undefined, referringUrl: undefined } : undefined,
				});
			},
		};
	}, [trackingManager]);
}
